import type { OrgChartNode } from "@evolua/api-client";
import {
  Badge,
  EmptyState,
  ErrorState,
  Icon,
  IconButton,
  PageHeader,
  SearchField,
  Skeleton,
} from "@evolua/ui";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { getApiClient } from "../auth/http.js";
import { getDemoOrgChart, getDemoOrgChartPending } from "../api/demo-orgchart.js";
import { queryKeys } from "../api/query-keys.js";
import { useSession } from "../session/SessionContext.js";

function NodeRow({
  node,
  depth,
  expanded,
  onToggle,
  childrenMap,
}: {
  node: OrgChartNode;
  depth: number;
  expanded: Set<string>;
  onToggle: (id: string) => void;
  childrenMap: Map<string, OrgChartNode[]>;
}) {
  const isOpen = expanded.has(node.id);
  const kids = childrenMap.get(node.id) ?? node.children;
  return (
    <li>
      <div className="evolua-org-tree__node">
        {node.childrenCount > 0 ? (
          <IconButton
            label={isOpen ? "Recolher" : "Expandir"}
            onClick={() => onToggle(node.id)}
          >
            <Icon name={isOpen ? "chevron-down" : "chevron-right"} size={16} />
          </IconButton>
        ) : (
          <span className="evolua-org-tree__spacer" aria-hidden="true" />
        )}
        <Link to={`/pessoas/${node.id}`}>{node.fullName}</Link>
        <Badge tone="neutral">{node.positionName ?? "—"}</Badge>
        {node.orphan ? <Badge tone="warning">sem gestor</Badge> : null}
        <span className="evolua-muted">
          {node.departmentName} · {node.childrenCount} direto(s)
        </span>
      </div>
      {isOpen && kids.length > 0 ? (
        <ul className="evolua-org-tree">
          {kids.map((c) => (
            <NodeRow
              key={c.id}
              node={c}
              depth={depth + 1}
              expanded={expanded}
              onToggle={onToggle}
              childrenMap={childrenMap}
            />
          ))}
        </ul>
      ) : null}
    </li>
  );
}

/** Organograma de pessoas (FASE-06-T30). */
export function OrgChartPage() {
  const { isDemoMode } = useSession();
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const [childrenMap, setChildrenMap] = useState<Map<string, OrgChartNode[]>>(
    () => new Map(),
  );

  const rootsQuery = useQuery({
    queryKey: [...queryKeys.org.all, "orgchart", q],
    queryFn: () =>
      isDemoMode
        ? getDemoOrgChart(q.trim() ? { q: q.trim() } : {})
        : getApiClient().getOrgChart(q.trim() ? { q: q.trim() } : {}),
  });

  const pendingQuery = useQuery({
    queryKey: [...queryKeys.org.all, "orgchart-pending"],
    queryFn: () =>
      isDemoMode ? getDemoOrgChartPending() : getApiClient().getOrgChartPending(),
  });

  const roots = rootsQuery.data?.data ?? [];

  const searchMode = useMemo(
    () => rootsQuery.data?.mode === "search",
    [rootsQuery.data?.mode],
  );

  async function toggle(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
        return next;
      }
      next.add(id);
      return next;
    });
    if (childrenMap.has(id)) return;
    const res = isDemoMode
      ? getDemoOrgChart({ parentId: id })
      : await getApiClient().getOrgChart({ parentId: id });
    setChildrenMap((prev) => {
      const next = new Map(prev);
      next.set(id, res.data);
      return next;
    });
  }

  const lead = rootsQuery.data
    ? `Hierarquia por gestor. Expansão sob demanda (mobile-friendly). ${rootsQuery.data.totalEmployees} ativos · ${rootsQuery.data.pendingWithoutManager} sem gestor`
    : "Hierarquia por gestor. Expansão sob demanda (mobile-friendly).";

  return (
    <div className="evolua-page">
      <PageHeader title="Organograma" lead={lead} />

      <SearchField
        label="Buscar colaborador"
        placeholder="Buscar colaborador"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        aria-label="Buscar no organograma"
      />

      {rootsQuery.isLoading ? <Skeleton height="7.5rem" label="Organograma" /> : null}
      {rootsQuery.isError ? (
        <ErrorState
          title="Falha ao carregar organograma"
          onRetry={() => void rootsQuery.refetch()}
        />
      ) : null}

      {!rootsQuery.isLoading && !roots.length ? (
        <EmptyState
          title="Nenhum nó"
          description="Ajuste a busca ou sincronize a base de pessoas."
        />
      ) : (
        <ul className="evolua-org-tree">
          {roots.map((n) =>
            searchMode ? (
              <li key={n.id}>
                <Link to={`/pessoas/${n.id}`}>{n.fullName}</Link>{" "}
                <Badge tone="neutral">{n.departmentName ?? "—"}</Badge>
              </li>
            ) : (
              <NodeRow
                key={n.id}
                node={n}
                depth={0}
                expanded={expanded}
                onToggle={(id) => void toggle(id)}
                childrenMap={childrenMap}
              />
            ),
          )}
        </ul>
      )}

      <section className="evolua-stack evolua-stack--3">
        <h2 className="evolua-section-title">Pendências sem gestor</h2>
        {pendingQuery.isLoading ? <Skeleton height="3.75rem" label="Pendências" /> : null}
        {pendingQuery.isError ? (
          <ErrorState
            title="Falha ao carregar pendências"
            onRetry={() => void pendingQuery.refetch()}
          />
        ) : null}
        {!pendingQuery.isLoading && (pendingQuery.data?.data.length ?? 0) === 0 ? (
          <EmptyState
            title="Nenhuma pendência"
            description="Todos os colaboradores ativos têm gestor atribuído."
          />
        ) : (
          <ul className="evolua-stack evolua-stack--3">
            {(pendingQuery.data?.data ?? []).map((p) => (
              <li key={p.id}>
                <Link to={`/pessoas/${p.id}`}>{p.fullName}</Link> — {p.departmentName}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
